import { PlusIcon } from '@heroicons/react/24/outline';
import { keepPreviousData } from '@tanstack/react-query';
import {
  type ColumnFiltersState,
  type ColumnVisibilityState,
  createColumnHelper,
  flexRender,
  type SortingState,
  useTable,
} from '@tanstack/react-table';
import clsx from 'clsx';
import { useState } from 'react';
import { Link } from 'react-router';
import { useEdiPublishedNoticeList, useEdiPurchaseOrderList } from '@/api/hanly-v2/edi/edi';
import type { EdiPurchaseOrderList, EdiPurchaseOrderListParams } from '@/api/hanly-v2/hanlyAPIV2.schemas';
import { Breadcrumbs } from '@/components/breadcrumbs';
import { Button } from '@/components/ui/button';
import { FilterPanel, FilterToggleButton } from '@/components/ui/filter-panel';
import { Pagination } from '@/components/ui/pagination';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { type SortOption, SortSelect } from '@/components/ui/sort-select';
import { ColumnHeader } from '@/components/ui/table/column-header';
import { useShowPrices } from '@/contexts/auth';
import { NoticeSection } from '@/features/notices/notice-section';
import { NewOrderStartSheet } from '@/features/orders/new-order-start-sheet';
import { StatusBadge, statusLabels } from '@/features/orders/status-badge';
import { ediTableFeatures } from '@/libs/table';
import { formatYMDhm, formatYMDSlash } from '@/utils/date';

const statusTabs = ['すべて', 'PENDING', 'CONFIRMED'] as const;
type StatusTab = (typeof statusTabs)[number];

const statusTabOptions = statusTabs.map((tab) => ({
  value: tab,
  label: tab === 'すべて' ? tab : statusLabels[tab],
}));

const formatDesiredDeliveryDate = (value: string | null | undefined) =>
  value ? formatYMDSlash(value) : '即納希望';

const columnHelper = createColumnHelper<typeof ediTableFeatures, EdiPurchaseOrderList>();

// columns() で包むと、列ごとの TValue を保ったまま useTable に渡せる。
export const columns = columnHelper.columns([
  columnHelper.accessor('id', {
    header: ({ column }) => <ColumnHeader column={column} title="EDI No." />,
    cell: (info) => info.getValue(),
    meta: { filterVariant: 'text' as const, label: 'EDI No.', placeholder: '例）1' },
    size: 130,
  }),
  columnHelper.accessor('created_at', {
    header: ({ column }) => <ColumnHeader column={column} title="注文日時" />,
    cell: (info) => formatYMDhm(info.getValue()),
    meta: { filterVariant: 'dateRange' as const, label: '注文日' },
    size: 160,
  }),
  columnHelper.accessor('desired_delivery_date', {
    header: ({ column }) => <ColumnHeader column={column} title="希望納期" />,
    cell: (info) => formatDesiredDeliveryDate(info.getValue()),
    meta: { filterVariant: 'dateRange' as const, label: '希望納期' },
    size: 128,
  }),
  columnHelper.accessor('total_amount', {
    header: ({ column }) => <ColumnHeader column={column} title="合計金額" className="justify-end" />,
    cell: (info) => (
      <div className="text-right font-bold">
        <span className="text-sm">￥</span>
        {Number(info.getValue()).toLocaleString()}
      </div>
    ),
    meta: { filterVariant: 'range' as const, label: '合計金額' },
    size: 157,
  }),
  columnHelper.accessor('orderer_name', {
    header: ({ column }) => <ColumnHeader column={column} title="注文担当者" />,
    cell: (info) => info.getValue(),
    meta: { filterVariant: 'text' as const, label: '注文担当者', placeholder: '例）山田' },
    size: 157,
  }),
  columnHelper.accessor('status', {
    header: ({ column }) => <ColumnHeader column={column} title="ステータス" />,
    cell: (info) => <StatusBadge status={info.getValue()} />,
    // ステータスはタブで絞り込むため、絞り込み欄は出さない
    size: 128,
  }),
]);

const PAGE_SIZE = 50;

/** テーブルの列IDとAPIのソートキー・絞り込みパラメータの対応 */
/** ソートキーの対応。ColumnHeader を出している列は全てここに載せる(載せ忘れると
 *  矢印だけ動いて並び替わらない) */
const COLUMN_TO_PARAM = {
  id: 'id',
  created_at: 'created_at',
  desired_delivery_date: 'desired_delivery_date',
  total_amount: 'total_amount',
  orderer_name: 'orderer_name',
  status: 'status',
} as const;

/** 部分一致で絞り込む列 */
const TEXT_FILTER_COLUMNS = ['id', 'orderer_name'] as const;
/** 数値の min/max で絞り込む列 */
const NUMERIC_RANGE_COLUMNS = ['total_amount'] as const;
/** カレンダーで from/to の範囲を絞り込む列 */
const DATE_RANGE_COLUMNS = ['created_at', 'desired_delivery_date'] as const;

type TextFilterColumn = (typeof TEXT_FILTER_COLUMNS)[number];
type NumericRangeColumn = (typeof NUMERIC_RANGE_COLUMNS)[number];
type DateRangeColumn = (typeof DATE_RANGE_COLUMNS)[number];

const isTextFilterColumn = (id: string): id is TextFilterColumn =>
  (TEXT_FILTER_COLUMNS as readonly string[]).includes(id);
const isNumericRangeColumn = (id: string): id is NumericRangeColumn =>
  (NUMERIC_RANGE_COLUMNS as readonly string[]).includes(id);
const isDateRangeColumn = (id: string): id is DateRangeColumn =>
  (DATE_RANGE_COLUMNS as readonly string[]).includes(id);

export const toOrdering = (sorting: SortingState): EdiPurchaseOrderListParams['ordering'] => {
  const [first] = sorting;
  if (!first) return undefined;
  const key = COLUMN_TO_PARAM[first.id as keyof typeof COLUMN_TO_PARAM];
  if (!key) return undefined;
  return (first.desc ? `-${key}` : key) as EdiPurchaseOrderListParams['ordering'];
};

export const toFilterParams = (columnFilters: ColumnFiltersState): EdiPurchaseOrderListParams => {
  const params: EdiPurchaseOrderListParams = {};
  for (const filter of columnFilters) {
    if (isTextFilterColumn(filter.id)) {
      if (filter.value) params[filter.id] = String(filter.value);
      continue;
    }
    // 金額列は [最小, 最大] を min/max のパラメータに割り当てる
    if (isNumericRangeColumn(filter.id) && Array.isArray(filter.value)) {
      const [min, max] = filter.value as (number | undefined)[];
      if (min !== undefined) params[`${filter.id}_min`] = String(min);
      if (max !== undefined) params[`${filter.id}_max`] = String(max);
      continue;
    }
    // 日付列は [開始日, 終了日] を from/to のパラメータに割り当てる
    if (isDateRangeColumn(filter.id) && Array.isArray(filter.value)) {
      const [from, to] = filter.value as (string | undefined)[];
      if (from) params[`${filter.id}_from`] = from;
      if (to) params[`${filter.id}_to`] = to;
    }
  }
  return params;
};

/** スマホの並べ替え。PC は表の見出しで並べ替える */
const sortOptions: SortOption[] = [
  { value: '', label: '注文日時が新しい順', sorting: [] },
  { value: 'created_at', label: '注文日時が古い順', sorting: [{ id: 'created_at', desc: false }] },
  {
    value: 'desired_delivery_date',
    label: '希望納期が近い順',
    sorting: [{ id: 'desired_delivery_date', desc: false }],
  },
  { value: '-total_amount', label: '合計金額が高い順', sorting: [{ id: 'total_amount', desc: true }] },
];

const OrderCard = ({ order, showPrices }: { order: EdiPurchaseOrderList; showPrices: boolean }) => (
  <li>
    <Link
      to={`/orders/${order.id}`}
      className="flex flex-col gap-2 border border-neutral-200 bg-white px-4 py-3"
    >
      <div className="flex items-center justify-between">
        <span className="text-sm text-neutral-600">EDI No. {order.id}</span>
        <StatusBadge status={order.status} />
      </div>
      {showPrices && (
        <div className="flex items-baseline justify-between">
          <span className="text-xl font-bold">¥{Number(order.total_amount).toLocaleString()}</span>
        </div>
      )}
      <dl className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-sm">
        <div className="flex gap-1">
          <dt className="text-neutral-600">注文日時</dt>
          <dd>{formatYMDhm(order.created_at)}</dd>
        </div>
        <div className="flex gap-1">
          <dt className="text-neutral-600">希望納期</dt>
          <dd>{formatDesiredDeliveryDate(order.desired_delivery_date)}</dd>
        </div>
        <div className="col-span-2 flex gap-1">
          <dt className="text-neutral-600">注文担当者</dt>
          <dd>{order.orderer_name}</dd>
        </div>
      </dl>
    </Link>
  </li>
);

export const OrderList = () => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [activeTab, setActiveTab] = useState<StatusTab>('PENDING');
  const [pageIndex, setPageIndex] = useState(0);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isStartSheetOpen, setIsStartSheetOpen] = useState(false);
  const showPrices = useShowPrices();
  // 料金を表示しない発注先は合計金額の列・並べ替えを出さない(サーバも合計金額を null で返し、絞り込み・並べ替えを受け付けない)
  const columnVisibility: ColumnVisibilityState = showPrices ? {} : { total_amount: false };
  const visibleSortOptions = showPrices
    ? sortOptions
    : sortOptions.filter((option) => option.sorting.every((sort) => sort.id !== 'total_amount'));

  const { data, isLoading, isError } = useEdiPurchaseOrderList(
    {
      page: pageIndex + 1,
      page_size: PAGE_SIZE,
      ...(activeTab === 'すべて' ? {} : { status: activeTab }),
      ordering: toOrdering(sorting),
      ...toFilterParams(columnFilters),
    },
    // 絞り込み・並べ替え・ページ送りのたびにクエリキーが変わる。前のデータを保持しないと
    // 再取得中に一覧(と検索欄)がアンマウントされ、1文字入力するたびにフォーカスが外れる。
    { query: { placeholderData: keepPreviousData } },
  );
  const orders = data?.results ?? [];
  const { data: noticesData, isError: isNoticesError } = useEdiPublishedNoticeList();
  const notices = isNoticesError ? [] : (noticesData ?? []);
  const hasFilter = columnFilters.length > 0;
  const totalCount = data?.count ?? 0;

  // タブ・ソート・絞り込みを変えたら先頭ページに戻す
  const resetToFirstPage = () => setPageIndex(0);

  const table = useTable({
    features: ediTableFeatures,
    data: orders,
    columns,
    state: { sorting, columnFilters, columnVisibility },
    onSortingChange: (updater) => {
      setSorting(updater);
      resetToFirstPage();
    },
    onColumnFiltersChange: (updater) => {
      setColumnFilters(updater);
      resetToFirstPage();
    },
    manualSorting: true,
    manualFiltering: true,
  });

  const statusMessage = isLoading
    ? { text: '読み込み中...', className: 'text-neutral-600' }
    : isError
      ? { text: 'データの取得に失敗しました', className: 'text-red-500' }
      : orders.length === 0
        ? {
            text: hasFilter ? '該当する発注がありません' : '発注データがありません',
            className: 'text-neutral-600',
          }
        : null;

  return (
    <div className="flex-1">
      <Breadcrumbs items={[{ label: 'ホーム', to: '/' }, { label: '発注' }]} />

      <div className="flex flex-col gap-3 px-4 pt-4 pb-24 md:px-8 md:pb-6">
        <h2 className="md:hidden">発注</h2>
        <NoticeSection notices={notices} limit={3} className="hidden md:block" />
        <div className="flex items-center justify-between gap-3">
          <SegmentedControl
            ariaLabel="ステータス"
            options={statusTabOptions}
            value={activeTab}
            onChange={(tab) => {
              setActiveTab(tab);
              resetToFirstPage();
            }}
            className="w-full md:w-80"
          />
          <Button onClick={() => setIsStartSheetOpen(true)} className="hidden h-10 md:flex">
            <PlusIcon className="size-5" />
            新規発注
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <FilterToggleButton
            open={isFilterOpen}
            onToggle={() => setIsFilterOpen((open) => !open)}
            activeCount={columnFilters.length}
          />
          <SortSelect
            options={visibleSortOptions}
            sorting={sorting}
            onChange={table.setSorting}
            defaultSortValue={'-created_at'}
            className="h-10 min-w-0 cursor-pointer border border-neutral-300 bg-white px-2 text-sm md:hidden"
          />
        </div>
        {isFilterOpen && <FilterPanel table={table} />}
        <NoticeSection notices={notices} limit={2} className="md:hidden" />

        <ul className="flex flex-col gap-2 md:hidden">
          {statusMessage ? (
            <li className={clsx('py-8 text-center', statusMessage.className)}>{statusMessage.text}</li>
          ) : (
            orders.map((order) => <OrderCard key={order.id} order={order} showPrices={showPrices} />)
          )}
        </ul>

        <div className="hidden overflow-x-auto md:block">
          <table className="w-full border border-neutral-200">
            <thead className="sticky top-0">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="whitespace-nowrap">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="bg-neutral-100 px-3 py-2 text-left text-sm font-normal text-neutral-600"
                      style={{ width: header.getSize() }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {statusMessage ? (
                <tr>
                  <td
                    colSpan={table.getVisibleLeafColumns().length}
                    className={clsx('py-8 text-center', statusMessage.className)}
                  >
                    {statusMessage.text}
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="group">
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="whitespace-nowrap text-neutral-900 group-hover:bg-neutral-100"
                      >
                        <Link to={`/orders/${row.original.id}`} className="block px-3 py-2">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </Link>
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          pageIndex={pageIndex}
          pageSize={PAGE_SIZE}
          totalCount={totalCount}
          onPageChange={setPageIndex}
        />
      </div>

      <button
        type="button"
        onClick={() => setIsStartSheetOpen(true)}
        className="fixed right-4 bottom-20 z-20 flex h-13 cursor-pointer items-center gap-1.5 rounded bg-sky-800 pr-5 pl-4 font-bold text-white shadow-lg md:hidden"
      >
        <PlusIcon className="size-5" />
        新規発注
      </button>

      <NewOrderStartSheet open={isStartSheetOpen} onClose={() => setIsStartSheetOpen(false)} />
    </div>
  );
};
